<template src="@/components/templates/signup.html"></template>

<script>
import FileUpload from "vue-upload-component"
import Pica from "pica"
import Cropper from "cropperjs"
import Datepicker from "vuejs-datepicker"
import PlayerUser from "@/models/PlayerUser"
import STATUS_CODES from "@/common/statusCodes"
import { TOASTER_SUCCESS, TOASTER_ERROR } from "@/common/events.types"
import moment from "moment"
import {
  TERMS_URL,
  PRIVACY_POLICY_URL,
} from "@/constants/RouterConstants"
import { FETCH_SIGN_UP_DATA, SIGN_UP, EMAIL_CHECK, ADD_ADDITIONAL_EMAIL } from "@/store/action.types"
import { mapActions, mapGetters } from "vuex"
import _ from "lodash"
import { en, fr } from "vuejs-datepicker/dist/locale"
import { UPDATE_LANGUAGE } from "@/store/action.types"
import intlTelInput from "intl-tel-input"
import "intl-tel-input/build/css/intlTelInput.css"
import TagInput from "@/components/common/form/TagInput.vue";
import { UNIT_SYSTEM } from '../../../constants/UnitConsts'

import {
  IN_PER_FT,
  toNum,
  resolveHeightCm,
  resolveWeightKg,
  resolveMetricHeightCm,
  resolveMetricWeightKg,
  heightDisplay,
  weightDisplay
} from '@/common/units'
import { ageInYears, toDateOnly, toIsoDateString, withDateParts } from '@/common/dates'
import { PARENTAL_CONSENT_AGE } from '@/constants/AgeConsts'

export default {
  name: "Signup",
  data() {
    return {
      files: [],
      edit: false,
      cropper: false,
      initialPlayerEmail: "",
      playerUser: new PlayerUser(),
      pageLoaded: false,
      linkIsInvalid: false,
      formSubmitted: false,
      TERMS_URL,
      PRIVACY_POLICY_URL,
      emailValid: true,
      parentEmailValid: true,
      invalidLinkMessage: "",
      maxImageSize: 500,
      avatarLoading: false,
      resizingImageInProgress: false,
      formButtonLoading: false,
      redeemedLink: false,
      en: en,
      fr: fr,
      feetInput: "",
      inchInput: "",
      phoneValid: null,
      parentPhoneValid: null,
      intlTelInputInitialized: false,
      intlTelInputInitializedParent: false,
      countryCode: null,
      parentPhoneNumber: "",
      parentCountryCode: "",
      additionalEmails: {
        emails: [],
        invalid: false
      },
      playerIdOnLoad: null,
      // Stored (metric) values; the inputs may be showing them in imperial
      metricHeight: null,
      metricWeight: null
    }
  },
  computed: {
    ...mapGetters(["languageChangedOnSignUp", "user"]),
    signUpToken: {
      get() {
        return this.$route.params.token
      },
    },
    dayOfBirth: {
      get() {
        return moment(this.playerUser.dateOfBirth, "D")
      },
      set(newDay) {
        this.playerUser.dateOfBirth = newDay
      },
    },
    monthOfBirth: {
      get() {
        return moment(this.playerUser.dateOfBirth, "M")
      },
      set(newDay) {
        this.playerUser.monthOfBirth = newDay
      },
    },
    yearOfBirth: {
      get() {
        return moment(this.playerUser.dateOfBirth, "Y")
      },
      set(newDay) {
        this.playerUser.yearOfBirth = newDay
      },
    },
    isMetric() {
      return (this.playerUser.unitHeightWeight || UNIT_SYSTEM.IMPERIAL) === UNIT_SYSTEM.METRIC;
    },
    isImperial() {
      return !this.isMetric;
    },
    isDistanceImperial: {
      get() {
        const u = this.playerUser.unitDistance == null
          ? UNIT_SYSTEM.METRIC
          : this.playerUser.unitDistance;
        return u === UNIT_SYSTEM.IMPERIAL;
      },
      set(val) {
        this.playerUser.unitDistance = val
          ? UNIT_SYSTEM.IMPERIAL
          : UNIT_SYSTEM.METRIC;
      }
    },
    parentalConsentNeeded: {
      get() {
        if (this.playerUser.lockedParent) return true;

        const age = ageInYears(this.playerUser.dateOfBirth);
        return age !== null && age < PARENTAL_CONSENT_AGE;
      }
    },
  },
  mounted() {
    this.$i18n.locale = this.$route.query.lang

    this.fetchSignUpDataAction(this.signUpToken)
      .then((response) => {
        if (response.status.code === STATUS_CODES.BP_200) {
          this.playerUser = new PlayerUser(response.data)
          this.playerUser.dateOfBirth = toDateOnly(this.playerUser.dateOfBirth);
          this.initialPlayerEmail = this.playerUser.email
          this.pageLoaded = true
          this.linkIsInvalid = false
          this.getPlayerIdOnLoad(response.data.playerId);
          this.$nextTick(() => {
            this.initHeightWeightUnits();
          });
        } else if (response.status.code === STATUS_CODES.BP_412) {
          this.redeemedLink = true
          this.linkIsInvalid = false
        } else if (response.status.code === STATUS_CODES.BP_405) {
          this.linkIsInvalid = true
        } else if (response.status.code === STATUS_CODES.BP_404) {
          this.linkIsInvalid = true
        }
      })
      .catch((error) => {
        this.linkIsInvalid = true
        this.pageLoaded = true
      })
  },
  created() {
    this.debounceInput = _.debounce(($event) => {
      let inputElement = $event.target
      let inputElementId = inputElement.getAttribute("id")
      let role = inputElementId === "email" ? "PLAYER" : "PARENT"
      let email =
        inputElementId === "email"
          ? this.playerUser.email
          : this.playerUser.parentEmail

      this.checkEmail({
        token: this.signUpToken,
        credentials: {
          email,
          role,
        },
      })
        .then((data) => {
          if (role === "PLAYER") {
            this.emailValid = data
          } else {
            this.parentEmailValid = data
          }
        })
        .catch(() => {
          if (role === "PLAYER") {
            this.emailValid = false
          } else {
            this.parentEmailValid = false
          }
        })
    }, 500)
  },
  updated() {
    const context = this
    this.$nextTick(function () {
      const input = document.querySelector("#phoneNumber")
      if (!this.intlTelInputInitialized && !this.linkIsInvalid && input) {
        this.intlTelInputInitialized = true
        this.iti = intlTelInput(input, {
          utilsScript:
            "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
          initialCountry: context.playerUser.phone.countryCode || "us",
          preferredCountries: ["us", "ca", "gb", "de"],
          nationalMode: true,
          autoPlaceholder: "off",
          formatOnDisplay: false,
        })

        let countryData = context.iti.getSelectedCountryData()
        this.countryCode = countryData.iso2

        function handleCountryChange() {
          let changedCountryData = context.iti.getSelectedCountryData()
          context.countryCode = changedCountryData.iso2
          context.phoneValid = context.iti.isValidNumber() ? true : false
          if (context.phoneValid) {
            context.$validator.errors.removeById("invalidPhone")
          } else if (!context.phoneValid && !context.playerUser.phone.number) {
            context.$validator.errors.removeById("invalidPhone")
          }
        }

        function handlePhoneChange() {
          context.phoneValid = context.iti.isValidNumber() ? true : false
          if (context.phoneValid) {
            context.$validator.errors.removeById("invalidPhone")
          } else if (!context.phoneValid && !context.playerUser.phone.number) {
            context.$validator.errors.removeById("invalidPhone")
          }
        }

        input.addEventListener("countrychange", handleCountryChange)
        input.addEventListener("change", handlePhoneChange)
      }

      if (
        !this.intlTelInputInitializedParent &&
        !this.linkIsInvalid &&
        this.parentalConsentNeeded
      ) {
        this.intlTelInputInitializedParent = true
        const parentPhoneInput = document.querySelector("#parentPhoneNumber")
        this.parentIti = intlTelInput(parentPhoneInput, {
          utilsScript:
            "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
          initialCountry: context.playerUser.phone.countryCode || "us",
          preferredCountries: ["us", "ca", "gb", "de"],
          nationalMode: true,
          autoPlaceholder: "off",
          formatOnDisplay: false,
        })

        let countryData = context.iti.getSelectedCountryData()
        this.parentCountryCode = countryData.iso2

        function handleParentCountryChange() {
          let changedCountryData = context.parentIti.getSelectedCountryData()
          context.parentCountryCode = changedCountryData.iso2
          context.parentPhoneValid = context.parentIti.isValidNumber()
            ? true
            : false
          if (context.parentPhoneValid) {
            context.$validator.errors.removeById("invalidParentPhone")
          } else if (!context.parentPhoneValid && !context.parentPhoneNumber) {
            context.$validator.errors.removeById("invalidPhone")
          }
        }

        function handleParentPhoneChange() {
          context.parentPhoneValid = context.parentIti.isValidNumber()
            ? true
            : false
          if (context.parentPhoneValid) {
            context.$validator.errors.removeById("invalidParentPhone")
          } else if (!context.parentPhoneValid && !context.parentPhoneNumber) {
            context.$validator.errors.removeById("invalidPhone")
          }
        }

        parentPhoneInput.addEventListener(
          "countrychange",
          handleParentCountryChange
        )
        parentPhoneInput.addEventListener("change", handleParentPhoneChange)
      }
    })
  },
  beforeDestroy() {
    this.iti.destroy()
    if (this.parentalConsentNeeded) {
      this.parentIti.destroy()
    }
  },
  watch: {
    edit(value) {
      const context = this

      if (value) {
        this.$nextTick(function () {
          context.avatarLoading = true

          if (!this.$refs.editImage) {
            return
          }

          function loaded() {
            context.avatarLoading = false
          }

          if (this.$refs.editImage.complete) {
            loaded()
          } else {
            this.$refs.editImage.addEventListener("load", loaded)
            this.$refs.editImage.addEventListener("error", function () {
              console.log("error")
            })
          }

          let cropper = new Cropper(this.$refs.editImage, {
            viewMode: 1,
          })

          this.cropper = cropper
        })
      } else {
        if (this.cropper) {
          this.cropper.destroy()
          this.cropper = false
        }
      }
    },
  },
  methods: {
    ...mapActions({
      fetchSignUpDataAction: FETCH_SIGN_UP_DATA,
      signUpAction: SIGN_UP,
      checkEmail: EMAIL_CHECK,
      updateLanguageAction: UPDATE_LANGUAGE,
      addAditionalEmailAction: ADD_ADDITIONAL_EMAIL
    }),

    toDateOnly,
    toIsoDateString,
    initHeightWeightUnits() {

      if (this.playerUser.unitHeightWeight == null) {
        this.playerUser.unitHeightWeight = UNIT_SYSTEM.IMPERIAL;
      }

      this.metricHeight = toNum(this.playerUser.height) || null;
      this.metricWeight = toNum(this.playerUser.weight) || null;
      this.renderHeightWeightInputs();
    },

    renderHeightWeightInputs() {
      const imperial = !this.isMetric;
      const height = heightDisplay(this.metricHeight, imperial);
      if (imperial) {
        this.feetInput = height.ft;
        this.inchInput = height.inch;
      } else {
        this.playerUser.height = height;
      }
      this.playerUser.weight = weightDisplay(this.metricWeight, imperial);
    },

    // Updates the stored metric values from whatever the inputs currently show
    syncMetricFromInputs() {
      if (this.isMetric) {
        this.metricHeight = resolveMetricHeightCm(this.metricHeight, this.playerUser.height);
        this.metricWeight = resolveMetricWeightKg(this.metricWeight, this.playerUser.weight);
      } else {
        this.metricHeight = resolveHeightCm(this.metricHeight, this.feetInput, this.inchInput);
        this.metricWeight = resolveWeightKg(this.metricWeight, this.playerUser.weight);
      }
    },

    normalizeInchesOverflow() {
      let ft = Number(this.feetInput);
      let inch = Number(this.inchInput);
      if (!isNaN(ft) && !isNaN(inch) && inch >= IN_PER_FT) {
        ft += Math.floor(inch / IN_PER_FT);
        inch = inch % IN_PER_FT;
        this.feetInput = ft;
        this.inchInput = inch;
      }
    },
    editAndResize() {
      this.resizingImageInProgress = true
      const context = this

      setTimeout(function () {
        context.editSave()
      }, 100)
    },
    editSave() {
      const pica = Pica()
      this.edit = false
      let oldFile = this.files[0]
      let canvasImg = this.cropper.getCroppedCanvas()

      const resizedCanvas = document.createElement("canvas")
      resizedCanvas.height = 500
      resizedCanvas.width = 500

      pica.resize(canvasImg, resizedCanvas).then((res) => {
        let binStr = atob(res.toDataURL(oldFile.type).split(",")[1])
        let arr = new Uint8Array(binStr.length)
        for (let i = 0; i < binStr.length; i++) {
          arr[i] = binStr.charCodeAt(i)
        }
        let file = new File([arr], oldFile.name, { type: oldFile.type })
        this.$refs.upload.update(oldFile.id, {
          file,
          type: file.type,
          size: file.size,
          active: true,
        })
        this.playerUser.file = file
        this.resizingImageInProgress = false
      })
    },
    inputFile(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        this.$nextTick(() => {
          this.edit = true
        })
      }
      if (!newFile && oldFile) {
        this.edit = false
      }
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (!/\.(gif|jpg|jpeg|png|webp)$/i.test(newFile.name)) {
          return prevent()
        }
      }

      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        newFile.url = ""
        let URL = window.URL || window.webkitURL
        if (URL && URL.createObjectURL) {
          newFile.url = URL.createObjectURL(newFile.file)
        }
      }
    },
    onDaySelect(date) {
      this.playerUser.dateOfBirth = withDateParts(this.playerUser.dateOfBirth, { day: date.getDate() });
    },
    onMonthSelect(date) {
      this.playerUser.dateOfBirth = withDateParts(this.playerUser.dateOfBirth, { month: date.getMonth() });
    },
    onYearSelect(date) {
      this.playerUser.dateOfBirth = withDateParts(this.playerUser.dateOfBirth, { year: date.getFullYear() });
    },
    onDistanceUnitChange() {
      this.isDistanceImperial = !this.isDistanceImperial;
    },
    convertMetricAndLog() {
      this.syncMetricFromInputs();
      this.playerUser.unitHeightWeight = this.isMetric ? UNIT_SYSTEM.IMPERIAL : UNIT_SYSTEM.METRIC;
      this.renderHeightWeightInputs();

      this.$amplitude.logEvent('pwa: change signup measurement unit', {
        unit: this.playerUser.unitHeightWeight
      });
    },
    validateAndSubmit() {
      let context = this
      this.phoneValid =
        this.iti.isValidNumber() || !this.playerUser.phone.number ? true : false

      this.$validator.validateAll().then((result) => {
        if (!context.phoneValid && context.playerUser.phone.number) {
          this.$validator.errors.add({
            field: "phoneNumber",
            msg: "Invalid Phone number.",
            id: "invalidPhone",
          })
        }

        if (this.parentalConsentNeeded) {
          this.parentPhoneValid =
            this.parentIti.isValidNumber() || !this.parentPhoneNumber
              ? true
              : false

          if (!context.parentPhoneValid && context.parentPhoneNumber) {
            this.$validator.errors.add({
              field: "parentPhone",
              msg: "Invalid Phone number.",
              id: "invalidParentPhone",
            })
          }
        }

        this.formSubmitted = true

        if (
          this.emailValid &&
          this.parentEmailValid &&
          !this.resizingImageInProgress &&
          !this.avatarLoading &&
          this.phoneValid
        ) {
          let playerUser = this.playerUser
          let parentalConsentNeeded = this.parentalConsentNeeded

          if (!result || !playerUser.termsAndPolicyConfirmed) {
            this.formSubmitted = true
          } else if (
            parentalConsentNeeded &&
            !playerUser.legalGuardianConfirmed
          ) {
            this.formSubmitted = true
          } else if (parentalConsentNeeded && !context.parentPhoneValid) {
            this.formSubmitted = true
          } else {
            this.formButtonLoading = true
            let playerUserFormData = new FormData()

            // Height/weight are sent from the metric values so the inputs keep showing what was typed
            this.syncMetricFromInputs();

            this.playerUser.unitHeightWeight = this.isMetric ? UNIT_SYSTEM.METRIC : UNIT_SYSTEM.IMPERIAL;

            const dobStr = toIsoDateString(this.playerUser.dateOfBirth);

            for (let property in playerUser) {
              if (
                playerUser[property] &&
                property !== "phone" &&
                property !== "parentPhone" &&
                property !== "dateOfBirth" &&
                property !== "height" &&
                property !== "weight"
              ) {
                playerUserFormData.append(property, playerUser[property]);
              }
            }

            if (this.metricHeight != null) playerUserFormData.append("height", this.metricHeight);
            if (this.metricWeight != null) playerUserFormData.append("weight", this.metricWeight);

            playerUserFormData.append("dateOfBirth", dobStr);

            if (this.playerUser.phone.number) {
              playerUserFormData.append(
                "phone.countryCode",
                this.countryCode.toUpperCase()
              )
              playerUserFormData.append(
                "phone.number",
                this.playerUser.phone.number
              )
            }

            if (this.parentPhoneNumber) {
              playerUserFormData.append(
                "parentPhone.countryCode",
                this.parentCountryCode.toUpperCase()
              )
              playerUserFormData.append(
                "parentPhone.number",
                this.parentPhoneNumber
              )
            }

            this.signUpAction({
              data: playerUserFormData,
              token: this.signUpToken,
            })
              .then((user) => {
                // The backend decides whether the email actually changed (case-insensitively);
                // BP_431 means it sent a confirmation email and the account is not active yet.
                const emailNeedsConfirmation =
                  user.status && user.status.code === STATUS_CODES.BP_431
                let toasterMsg = emailNeedsConfirmation
                  ? this.$t("OUTER_PAGES.signupPage.toastMessages.emailChanged")
                  : this.$t("OUTER_PAGES.signupPage.toastMessages.success")
                this.$eventHub.$emit(TOASTER_SUCCESS, { message: toasterMsg })
                this.formButtonLoading = false
                const userDetails = user.userDetails

                if (this.languageChangedOnSignUp) {
                  this.updateLanguageAction({
                    accountId: userDetails.accountId,
                    language: this.$i18n.locale.toUpperCase(),
                  }).then((response) => {
                    this.user.language = this.$i18n.locale.toUpperCase()
                    this.$router.push("/")
                  })
                } else {
                  this.$router.push("/")
                }

                // Additional mails checkup and sending from signup page
                if (!!this.additionalEmails.emails && user.userDetails.playerId) {
                  this.additionalEmails.emails.forEach(email => {
                    this.addAditionalEmailAction({
                      playerId: user.userDetails.playerId,
                      email
                    });
                  });
                }

                // Additional mails sending from signup page when parental consent is required
                if (this.parentalConsentNeeded) {
                  if (!!this.additionalEmails.emails && this.playerIdOnLoad) {
                    this.additionalEmails.emails.forEach(email => {
                      this.addAditionalEmailAction({
                        playerId: this.playerIdOnLoad,
                        email
                      });
                    });
                  }
                }
              })
              .catch((error) => {
                this.linkIsInvalid = true
                if (error.status.code === STATUS_CODES.BP_400) {
                  this.redeemedLink = false
                  context.$eventHub.$emit(TOASTER_ERROR, {
                    message: this.$t('TOAST_MESSAGES.errorValidData')
                  });
                } else if (error.status.code === STATUS_CODES.BP_403) {
                  this.redeemedLink = false
                  context.$eventHub.$emit(TOASTER_ERROR, {
                    message: this.$t('TOAST_MESSAGES.accountActivated')
                  });
                } else if (error.status.code === STATUS_CODES.BP_404) {
                  this.redeemedLink = false
                  context.$eventHub.$emit(TOASTER_ERROR, {
                    message: this.$t("TOAST_MESSAGES.errorTryAgain")
                  });
                } else {
                  this.pageLoaded = true
                  context.$eventHub.$emit(TOASTER_ERROR, {
                    message: this.$t('TOAST_MESSAGES.otherError')
                  });
                }

                this.formButtonLoading = false
              })
          }
        }
      })
    },
    toggleLegalGuardianConfirmation() {
      this.playerUser.legalGuardianConfirmed = !this.playerUser
        .legalGuardianConfirmed
    },
    toggleTermsAndPolicesConfrimation() {
      this.playerUser.termsAndPolicyConfirmed = !this.playerUser
        .termsAndPolicyConfirmed
    },
    clearUpload() {
      this.playerUser.file = null
      this.files = []
      this.$refs.upload.clear()
    },
    getEmailInputs(inputs) {
      this.additionalEmails = inputs;
    },
    getPlayerIdOnLoad(id) {
      this.playerIdOnLoad = id;
    }
  },
  components: {
    FileUpload,
    Cropper,
    Datepicker,
    TagInput
  },
}
</script>

<style lang="scss">
@import "@/scss/views/_signup.scss";

.form-item-wrapper {
  &.imperial-wrapper {
    margin-top: 9px;
  }

  .imperial-fields {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 600px;

    .imperial-fields-input {
      width: 45%;
    }

    .imperial-fields-units {
      padding-top: 15px;
      margin-bottom: 0;
      color: $mid-gray;
    }
  }

  .change-metric {
    margin-right: 10px;
    margin-bottom: 8px;
    border: 1px solid #697b94;

    .btn {
      cursor: pointer;
    }
  }

  .change-distance-unit {
    padding-top: 38px;
    padding-bottom: 12px;
  }

  .form-help {
    width: 20px;
    float: right;

    img {
      width: 100%;
    }
  }

  .parental-consent-title {
    margin-top: 40px;
  }

  @media (max-width: 767px) {
    .additional-mail-label {
      font-size: 14px;
    }
  }
}
</style>
